import React, { useCallback, useMemo, useState } from "react";

import { faEye, faPencil, faPlus } from "@fortawesome/free-solid-svg-icons";
import { faBook } from "@fortawesome/pro-light-svg-icons";
import { faFiles } from "@fortawesome/pro-regular-svg-icons";
import { useDispatch, useSelector } from "react-redux";

import { openDispatchingModal } from "actions";

import AgGridTable from "components/AgGrid/AgGridContainer";
import { RowSelectionMode } from "components/AgGrid/constants";
import { Column } from "components/Layout";

import { AgGridTables } from "constants/aggrid";
import { BusinessColId } from "constants/columnDefinitions";
import { ModalTypes } from "constants/navigation";
import {
  DeploymentPermission,
  SaleyardPermission,
} from "constants/permissions";

import {
  openCreateGlobalBusinessModal,
  openCreateSideLoadedBusinessModal,
  openEditBusinessModal,
  openModalLink,
} from "lib/navigation";
import { pluralize } from "lib/pluralize";

import {
  getDefaultBillingSettings,
  getIsLivestockAgent,
  getIsStaff,
  getProperties,
  selectDefaultBuyerSplitByBusinessIdLookup,
  selectDefaultVendorSplitByBusinessIdLookup,
} from "selectors";

import { selectBusinessesAggridData } from "selectors/aggrid/businesses";

import {
  useDebounceSelector,
  useHasBillingPermission,
  useHasDeploymentPermission,
  useHasExportBusinessesPermission,
  useHasSaleyardOrDeploymentPermission,
  useHasSideLoadedBusinessPermission,
  useIsMobile,
} from "hooks";
import { useSideLoadedSuggestionCountByBusinessId } from "hooks/useSideLoadedSuggestionCountByBusinessId";

import { columnsDefs, mobileColumnDefs } from "./BusinessListColumns";
import { withSideLoadedSuggestionCounts } from "./withSideLoadedSuggestionCounts";
import { faDownload } from "@fortawesome/pro-solid-svg-icons";

const getRowId = params => params.data.business.id;

const additionalActions = [
  {
    default: true,
    title: "Add Business",
    isDisabled: false,
    onClick: () => openEditBusinessModal(),
    icon: faPlus,
    dataTour: "addBusiness",
  },
];

export function BusinessTable() {
  const dispatch = useDispatch();
  const businessRows = useDebounceSelector(selectBusinessesAggridData);
  const [isSuggestionCountShown, setIsSuggestionCountShown] = useState(false);
  const sideLoadedSuggestionCountByBusinessId =
    useSideLoadedSuggestionCountByBusinessId(isSuggestionCountShown);
  const rowData = useMemo(
    () =>
      withSideLoadedSuggestionCounts(
        businessRows,
        sideLoadedSuggestionCountByBusinessId,
      ),
    [businessRows, sideLoadedSuggestionCountByBusinessId],
  );
  const onGridReady = useCallback(({ api }) => {
    const updateIsSuggestionCountShown = () =>
      setIsSuggestionCountShown(
        Boolean(
          api
            .getColumn(BusinessColId.SIDE_LOADED_SUGGESTION_COUNT)
            ?.isVisible(),
        ),
      );
    api.addEventListener(
      "displayedColumnsChanged",
      updateIsSuggestionCountShown,
    );
    updateIsSuggestionCountShown();
  }, []);
  const propertyByIdLookup = useSelector(getProperties);
  const defaultVendorSplitByBusinessIdLookup = useSelector(
    selectDefaultVendorSplitByBusinessIdLookup,
  );
  const defaultBuyerSplitByBusinessIdLookup = useSelector(
    selectDefaultBuyerSplitByBusinessIdLookup,
  );
  const hasExportCsvPermissions = useHasExportBusinessesPermission();
  const context = useMemo(
    () => ({
      defaultVendorSplitByBusinessIdLookup,
      defaultBuyerSplitByBusinessIdLookup,
      propertyByIdLookup,
    }),
    [
      defaultVendorSplitByBusinessIdLookup,
      defaultBuyerSplitByBusinessIdLookup,
      propertyByIdLookup,
    ],
  );

  const isMobile = useIsMobile();
  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const isStaff = useSelector(getIsStaff);
  const hasInterestFeature = useHasDeploymentPermission(
    DeploymentPermission.featureInterest,
  );
  const hasBillingPermission = useHasBillingPermission();
  const enableInterest = useSelector(getDefaultBillingSettings)?.enableInterest;
  const isInterestEnabled = useMemo(
    () => hasInterestFeature && enableInterest,
    [hasInterestFeature, enableInterest],
  );

  const hasMyobExportsFeature = useHasDeploymentPermission(
    DeploymentPermission.featureMyobIntegration,
  );
  const [selectedBusinessIds, setSelectedBusinessIds] = useState([]);

  const selectedBusinessIdsCount = selectedBusinessIds.length;
  const onRowSelectionChange = useCallback(
    rows => {
      const newSelectedBusinessIds = rows.map(r => r.business.id);
      if (
        newSelectedBusinessIds.length !== selectedBusinessIdsCount ||
        !newSelectedBusinessIds.every(id => selectedBusinessIds.includes(id))
      ) {
        setSelectedBusinessIds(newSelectedBusinessIds);
      }
    },
    [selectedBusinessIdsCount, selectedBusinessIds, setSelectedBusinessIds],
  );

  const defaultCsvExportParams = useMemo(
    () => ({
      // only export selected rows to the export csv - if no rows are selected export all
      onlySelected: !!selectedBusinessIdsCount,
    }),
    [selectedBusinessIdsCount],
  );

  const openEditBusinessesModal = useCallback(() => {
    dispatch(
      openDispatchingModal(
        ModalTypes.BulkUpdateBusiness,
        window.location.hash,
        {
          businessIds: selectedBusinessIds,
        },
      ),
    );
  }, [dispatch, selectedBusinessIds]);

  const onClickGenerateReports = useCallback(() => {
    openModalLink(ModalTypes.BusinessReports, {
      businessIds: selectedBusinessIds,
    });
  }, [selectedBusinessIds]);

  const onClickCalculateInterest = useCallback(() => {
    dispatch(
      openDispatchingModal(ModalTypes.InterestPreview, window.location.hash, {
        businessIds: selectedBusinessIds,
      }),
    );
  }, [dispatch, selectedBusinessIds]);

  const hasBulkUpdateBusinessPermission = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureBulkUpdateBusinesses,
    SaleyardPermission.featureBulkUpdateBusinesses,
  );
  const hasCreateGlobalBusinessFeature = useHasSaleyardOrDeploymentPermission(
    DeploymentPermission.featureGlobalBusinessSearch,
    SaleyardPermission.featureGlobalBusinessSearch,
  );

  const hasSideLoadBusinessFeature = useHasSideLoadedBusinessPermission();

  const trimmedActions = useMemo(() => {
    const actions = [...additionalActions];

    if (isLivestockAgent && hasBillingPermission) {
      actions.push({
        default: true,
        title: `Generate ${pluralize("Report", selectedBusinessIdsCount)} for ${selectedBusinessIdsCount} ${pluralize("Business", selectedBusinessIdsCount)}`,
        isDisabled: selectedBusinessIdsCount === 0,
        onClick: onClickGenerateReports,
        icon: faFiles,
        dataTour: "generateReports",
      });
    }
    if (hasCreateGlobalBusinessFeature) {
      actions.push({
        title: "Add Connected Business",
        isDisabled: false,
        onClick: () => openCreateGlobalBusinessModal(),
        icon: faBook,
        dataTour: "addGlobalBusiness",
      });
    }
    if (hasSideLoadBusinessFeature) {
      actions.push({
        title: "Add Business from Legacy Data",
        isDisabled: false,
        onClick: () => openCreateSideLoadedBusinessModal(),
        icon: faDownload,
        dataTour: "addSideLoadedBusiness",
      });
    }
    if (hasBulkUpdateBusinessPermission) {
      actions.push({
        dataTour: "bulkUpdate",
        title: `Bulk Update ${selectedBusinessIdsCount} Businesses`,
        onClick: () => openEditBusinessesModal(),
        icon: faPencil,
        isDisabled: selectedBusinessIdsCount === 0,
      });
    }
    if (isStaff && isInterestEnabled) {
      actions.push({
        title: `Calculate Interest for ${selectedBusinessIdsCount} ${pluralize("Business", selectedBusinessIdsCount)}`,
        isDisabled: selectedBusinessIdsCount === 0,
        onClick: onClickCalculateInterest,
        icon: faEye,
        dataTour: "calculateInterestForBusinesses",
      });
    }
    return actions;
  }, [
    hasCreateGlobalBusinessFeature,
    hasBillingPermission,
    hasBulkUpdateBusinessPermission,
    isLivestockAgent,
    isStaff,
    isInterestEnabled,
    openEditBusinessesModal,
    onClickGenerateReports,
    onClickCalculateInterest,
    selectedBusinessIdsCount,
    hasSideLoadBusinessFeature,
  ]);

  const memoizedColumnDefs = useMemo(
    () =>
      columnsDefs(
        isLivestockAgent,
        isInterestEnabled,
        hasMyobExportsFeature,
        hasBillingPermission,
        hasSideLoadBusinessFeature,
      ),
    [
      isLivestockAgent,
      isInterestEnabled,
      hasMyobExportsFeature,
      hasBillingPermission,
      hasSideLoadBusinessFeature,
    ],
  );

  return (
    <Column fullHeight>
      <AgGridTable
        additionalActions={trimmedActions}
        columnDefs={isMobile ? mobileColumnDefs : memoizedColumnDefs}
        context={context}
        getRowId={getRowId}
        onGridReady={onGridReady}
        rowData={rowData}
        tableName={AgGridTables.BUSINESSES}
        showTextFilter
        hasExportCsvPermissions={hasExportCsvPermissions}
        suppressCellSelection={!!isMobile}
        hideSideBar={!!isMobile}
        hideSavedViews={!!isMobile}
        rowsSelectable
        onRowSelectionChanged={onRowSelectionChange}
        rowSelection={RowSelectionMode.MULTIPLE}
        defaultCsvExportParams={defaultCsvExportParams}
        exportSaleTitle="Businesses"
      />
    </Column>
  );
}
